import { Controller } from "react-hook-form";
import { capitalize } from "lodash";
import { HexColorPicker, HexColorInput } from "react-colorful";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Textarea } from "@/app/admin/_common/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/admin/_common/ui/popover";
import { States } from "@/app/_utils/constants";
import { dayjsTz } from "@/app/_utils/dayjs";

const COLOR_PRESETS = [
  "#E11D48",
  "#EA580C",
  "#CA8A04",
  "#16A34A",
  "#0891B2",
  "#2563EB",
  "#7C3AED",
  "#DB2777",
  "#525252",
  "#000000",
];

interface GuestInputProps {
  control: any;
  errors: any;
  defaultValue: any;
}

export const FirstNameInput = ({ control, errors }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label htmlFor="first_name">First Name</Label>
    <Controller
      name="first_name"
      control={control}
      render={({ field }) => (
        <Input id="first_name" {...field} value={field.value ?? ""} placeholder="Johnny" />
      )}
    />
    {errors.first_name && <span className="text-[12px] text-red-600">{errors.first_name?.message}</span>}
  </div>
);

export const LastNameInput = ({ control, errors }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label htmlFor="last_name">Last Name</Label>
    <Controller
      name="last_name"
      control={control}
      render={({ field }) => (
        <Input id="last_name" {...field} value={field.value ?? ""} placeholder="Bravo" />
      )}
    />
    {errors.last_name && <span className="text-[12px] text-red-600">{errors.last_name?.message}</span>}
  </div>
);

export const InstagramInput = ({ control, errors }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label htmlFor="instagram">Instagram</Label>
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[13px] text-muted-foreground">
        @
      </span>
      <Controller
        name="instagram"
        control={control}
        render={({ field }) => (
          <Input
            id="instagram"
            {...field}
            value={field.value ?? ""}
            placeholder="comedy.uo"
            className="pl-7"
          />
        )}
      />
    </div>
    {errors.instagram && <span className="text-[12px] text-red-600">{errors.instagram?.message}</span>}
  </div>
);

export const IntakeInput = ({ defaultValue }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label>Intake</Label>
    <Input disabled value={defaultValue ?? ""} readOnly />
  </div>
);

export const OtherInput = ({ defaultValue }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label>Other</Label>
    <Input disabled value={defaultValue ?? ""} readOnly />
  </div>
);

export const DobInput = ({ defaultValue }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label>Date of Birth</Label>
    <Input
      type="date"
      disabled
      value={defaultValue ? dayjsTz(defaultValue).format("YYYY-MM-DD") : ""}
      readOnly
    />
  </div>
);

export const PhoneInput = ({ control, errors }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label htmlFor="phone">Phone</Label>
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[13px] text-muted-foreground">
        +1
      </span>
      <Controller
        name="phone"
        control={control}
        render={({ field }) => (
          <Input
            id="phone"
            {...field}
            value={field.value ?? ""}
            placeholder="3158778177"
            className="pl-8"
          />
        )}
      />
    </div>
    {errors.phone && <span className="text-[12px] text-red-600">{errors.phone?.message}</span>}
  </div>
);

export const EmailInput = ({ control, errors }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label htmlFor="email">Email</Label>
    <Controller
      name="email"
      control={control}
      render={({ field }) => (
        <Input id="email" {...field} value={field.value ?? ""} placeholder="someone@domain.com" />
      )}
    />
    {errors.email && <span className="text-[12px] text-red-600">{errors.email?.message}</span>}
  </div>
);

export const ColorInput = ({ control, defaultValue }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label htmlFor="color">Row Color</Label>
    <Controller
      name="color"
      control={control}
      render={({ field }) => {
        const color = field.value ?? defaultValue ?? "#ffffff";
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                id="color"
                type="button"
                className="h-9 w-16 cursor-pointer rounded-md border border-input bg-card p-1"
              >
                <span
                  className="block h-full w-full rounded-sm border border-border"
                  style={{ backgroundColor: color }}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[220px] p-3">
              <div className="flex flex-col gap-3">
                <HexColorPicker
                  color={color}
                  onChange={field.onChange}
                  style={{ width: "100%", height: 140 }}
                />
                <div className="flex flex-wrap gap-1.5">
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => field.onChange(preset)}
                      className="size-5 flex-none rounded-sm border border-border"
                      style={{ backgroundColor: preset }}
                      aria-label={preset}
                    />
                  ))}
                </div>
                <HexColorInput
                  prefixed
                  color={color}
                  onChange={field.onChange}
                  className="flex h-8 w-full rounded-md border border-input bg-card px-2.5 text-[12.5px] text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </PopoverContent>
          </Popover>
        );
      }}
    />
  </div>
);

export const NotesInput = ({ control, errors }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label htmlFor="notes">Notes</Label>
    <Controller
      name="notes"
      control={control}
      render={({ field }) => (
        <Textarea id="notes" {...field} value={field.value ?? ""} rows={3} />
      )}
    />
    {errors.notes && <span className="text-[12px] text-red-600">{errors.notes?.message}</span>}
  </div>
);

export const StateInput = ({ control, defaultValue }: GuestInputProps) => (
  <div className="grid gap-1.5">
    <Label>State</Label>
    <Controller
      name="state"
      control={control}
      render={({ field }) => (
        <Select
          value={field.value ? capitalize(field.value) : undefined}
          onValueChange={(value) => field.onChange(value?.toLowerCase())}
        >
          <SelectTrigger>
            <SelectValue placeholder={capitalize(defaultValue) || "Select a state"} />
          </SelectTrigger>
          <SelectContent>
            {States.map((state) => (
              <SelectItem key={state} value={state}>
                {state}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  </div>
);
